/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
/* Util from reshaped v1.7.0 *
 * minor changes:
 *    - document.activeElement is now of type HTMLElement
 *    - remove commented code in handleKeyDown method
 */
import * as keys from "../../constants/keys";
import { Chain } from "./Chain";
export const keyboardModeAttribute = 'data-rs-keyboard';
export const isKeyboardMode = () => document.body.hasAttribute(keyboardModeAttribute);
export const focusableSelector = 'a,button,input:not([type="hidden"]),textarea,select,details,[tabindex]:not([tabindex="-1"])';
const getActiveElement = () => {
    return document.activeElement;
};
const getFocusableElements = (el, extraElement) => {
    const focusableElements = Array.from(el.querySelectorAll(focusableSelector));
    const filteredElements = focusableElements.filter(el => {
        return !el.hasAttribute('disabled') && el.clientHeight > 0;
    });
    if (extraElement && filteredElements.length)
        filteredElements.unshift(extraElement);
    return filteredElements;
};
const getFocusData = (root, direction, extraElement) => {
    const focusable = getFocusableElements(root, extraElement);
    const focusableLimit = focusable.length - 1;
    const currentElement = getActiveElement();
    const currentIndex = focusable.indexOf(currentElement);
    let nextIndex = {
        next: currentIndex + 1,
        prev: currentIndex - 1,
        first: 0,
        last: focusableLimit,
    }[direction];
    const isOverflow = nextIndex > focusableLimit || nextIndex < 0;
    if (isOverflow)
        nextIndex = direction === 'prev' ? focusableLimit : 0;
    return { overflow: isOverflow, el: focusable[nextIndex] };
};
const focusElement = (root, target) => {
    const data = getFocusData(root, target);
    if (data.el)
        data.el.focus();
};
export const focusNextElement = (root) => focusElement(root, 'next');
export const focusPreviousElement = (root) => focusElement(root, 'prev');
export const focusFirstElement = (root) => focusElement(root, 'first');
export const focusLastElement = (root) => focusElement(root, 'last');
const trapScreenReader = (() => {
    let affectedElements = [];
    const applyHiddenToSiblings = (el) => {
        let sibling = el.parentNode && el.parentNode.firstChild;
        while (sibling) {
            const notCurrent = sibling !== el;
            const isValid = sibling.nodeType === 1 && !sibling.hasAttribute('aria-hidden');
            if (notCurrent && isValid) {
                sibling.setAttribute('aria-hidden', 'true');
                affectedElements.push(sibling);
            }
            sibling = sibling.nextSibling;
        }
    };
    const release = () => {
        affectedElements.forEach(el => {
            el.removeAttribute('aria-hidden');
        });
        affectedElements = [];
    };
    return (el) => {
        let currentEl = el;
        if (affectedElements.length)
            release();
        while (currentEl !== document.body) {
            applyHiddenToSiblings(currentEl);
            currentEl = currentEl.parentElement;
        }
        return { release };
    };
})();
export const trapFocus = (() => {
    let resetListeners = null;
    let srTrap = null;
    const chain = new Chain();
    return (root, options = {}) => {
        const { mode = 'dialog', focusFirstTrapElement = true, onNavigateOutside, includeTrigger, } = options;
        const triggerElement = getActiveElement();
        const isDialog = mode === 'dialog';
        const isContentMenu = mode === 'content-menu';
        const isTabMode = isDialog || isContentMenu;
        const isArrowsMode = mode === 'action-menu';
        let chainId = 0;
        // Release function for focusing and releasing the trap
        const release = (releaseOptions = {}) => {
            const { withoutFocusReturn } = releaseOptions;
            const keepFocusTrapped = !chain.isLast(chainId);
            if (keepFocusTrapped)
                return; // keep focus trapped, no need to release
            // We're keeping items in the chain when focus was preserver to have access to the original trigger later
            const releaseChainItem = chainId
                ? chain.removePreviousTill(chainId, item => document.body.contains(item.data.trigger))
                : undefined;
            // Get the trigger element that needs to receive focus _after_ the release of the trap
            const releaseTargetTrigger = (releaseChainItem === null || releaseChainItem === void 0 ? void 0 : releaseChainItem.trigger) || triggerElement;
            if (releaseTargetTrigger) {
                // Focus the trigger element, prevent scrolling if specified or NOT in keyboard mode
                releaseTargetTrigger.focus({ preventScroll: withoutFocusReturn || !isKeyboardMode() });
            }
            if (!resetListeners)
                return;
            resetListeners();
            if (srTrap)
                srTrap.release();
            resetListeners = null;
            srTrap = null;
            // if there are remaining items in the chain && the root (to trap), reapply the trap
            if (!chain.isEmpty && (releaseChainItem === null || releaseChainItem === void 0 ? void 0 : releaseChainItem.root)) {
                trapFocus(releaseChainItem.root);
            }
        };
        const handleKeyDown = (event) => {
            const key = event.key;
            const isTab = key === keys.TAB;
            const isNextTab = isTab && !event.shiftKey;
            const isBackTab = isTab && event.shiftKey;
            const isUp = isArrowsMode && key === keys.UP;
            const isDown = isArrowsMode && key === keys.DOWN;
            const isPrev = (isBackTab && isTabMode) || isUp;
            const isNext = (isNextTab && isTabMode) || isDown;
            const focusData = getFocusData(root, isPrev ? 'prev' : 'next', includeTrigger ? triggerElement : undefined);
            // Release the trap when tab is used in navigation modes that support arrows
            const hasNavigatedOutside = (isTab && isArrowsMode) || (isContentMenu && isTab && focusData.overflow);
            if (hasNavigatedOutside) {
                release();
                if (onNavigateOutside)
                    onNavigateOutside();
                return;
            }
            // We return after the last condition because Tab can be used for releasing in arrows mode
            if (!isPrev && !isNext)
                return;
            event.preventDefault();
            if (focusData.el)
                focusData.el.focus();
        };
        // Reset event listeners if focus is trapped elsewhere
        if (resetListeners)
            resetListeners();
        if (isDialog)
            srTrap = trapScreenReader(root);
        const focusable = getFocusableElements(root, includeTrigger ? triggerElement : undefined);
        if (!focusable.length)
            return null;
        chainId = chain.add({ root, trigger: triggerElement });
        if (focusFirstTrapElement)
            focusable[0].focus();
        document.addEventListener('keydown', handleKeyDown);
        resetListeners = () => document.removeEventListener('keydown', handleKeyDown);
        return release;
    };
})();
